import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, ExternalLink, Loader2 } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { TelegramIcon } from "@/components/icons/TelegramIcon";
import { useTelegramLink } from "./hooks/useTelegramLink";
import { useNotificationChannels } from "./hooks/useNotificationChannels";
import { TelegramManualFallback } from "./TelegramManualFallback";

const I18N = "profile.channels.telegram";

export interface TelegramHandoff {
  /** The minted deep link, or null while it is still being fetched. */
  url: string | null;
  /** No link yet and no error — the row shows a disabled Connect. */
  preparing: boolean;
  errored: boolean;
  rateLimited: boolean;
  /** True once the user has followed the link out to Telegram. */
  handedOff: boolean;
  markHandedOff: () => void;
  /**
   * A coarse pointer means the screen showing this page is the phone Telegram
   * is installed on: the link hands off to the app in place, and every desktop
   * rescue (scan a QR, open Telegram Web, paste a command) is noise there.
   */
  onPhone: boolean;
  troubleOpen: boolean;
  toggleTrouble: () => void;
}

/**
 * Owns everything the Telegram row needs that a generic channel row does not:
 * the prefetched link, whether the user has left for Telegram, and the poll
 * that notices when the webhook links them.
 */
export function useTelegramHandoff(options?: { enabled?: boolean }): TelegramHandoff {
  const [handedOff, setHandedOff] = useState(false);
  const [troubleOpen, setTroubleOpen] = useState(false);
  const onPhone = useMediaQuery("(pointer: coarse)");

  // A second observer on the shared contacts query: react-query polls at the
  // shortest interval any observer asks for, so this turns polling on for the
  // whole page while a handoff is outstanding and off again afterwards.
  const { data: channels } = useNotificationChannels({
    pollMs: handedOff ? 3000 : undefined,
  });
  const connected = (channels ?? []).some(
    (c) => c.provider === "telegram" && !c.revoked_at,
  );

  // Nothing to mint for someone already linked — nor for a surface that is
  // mounted on every page (the top bar) until the user actually opens it.
  const { data, isError, error } = useTelegramLink({
    enabled: !connected && (options?.enabled ?? true),
  });

  return {
    url: data?.url ?? null,
    preparing: !data?.url && !isError,
    errored: isError,
    rateLimited: isError && /rate_limited/i.test(error?.message ?? ""),
    handedOff,
    markHandedOff: () => setHandedOff(true),
    onPhone,
    troubleOpen,
    toggleTrouble: () => setTroubleOpen((open) => !open),
  };
}

/**
 * The Connect affordance for Telegram: a real link the user's own tap follows.
 *
 * On a phone it navigates in place — the tab *is* the app, so handing off
 * leaves Back pointing at the profile instead of stranding an orphan t.me tab.
 * On a pointer device the app page is worth keeping alive behind the handoff,
 * so it opens a tab.
 */
function TelegramConnectButton({ handoff, className }: { handoff: TelegramHandoff; className?: string }) {
  const { t } = useTranslation();

  // Once they have gone, the waiting panel below owns the retry. Leaving a
  // second affordance here — still labelled "Connect", a step they have
  // already taken — is the clutter this change exists to remove.
  if (handoff.handedOff) return null;

  // Capped at half the row and wrapped: uncapped, the sentence claimed the
  // whole row as its min-content width and crushed the "Telegram / Not
  // connected" label beside it to "T. N o." on a phone or in the 22rem
  // desktop profile column.
  if (handoff.errored) {
    return (
      <span className="max-w-[50%] text-right text-xs leading-snug text-error-500">
        {t(handoff.rateLimited ? `${I18N}.rateLimited` : `${I18N}.error`)}
      </span>
    );
  }

  if (!handoff.url) {
    return (
      <Button variant="secondary" size="sm" disabled className={className}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t("profile.channels.connect")}
      </Button>
    );
  }

  return (
    <a
      href={handoff.url}
      {...(handoff.onPhone ? {} : { target: "_blank", rel: "noopener noreferrer" })}
      onClick={handoff.markHandedOff}
      className={cn(buttonVariants({ variant: "secondary", size: "sm" }), className)}
    >
      {t("profile.channels.connect")}
    </a>
  );
}

/**
 * What the user comes back to. The old flow answered with a modal carrying a
 * QR, a Telegram Web link and a command to paste — three rescues presented as
 * if the normal path had failed. The rescues survive, one tap away, and only
 * on the devices where they can help.
 */
function TelegramWaiting({ handoff }: { handoff: TelegramHandoff }) {
  const { t } = useTranslation();
  if (!handoff.url) return null;

  return (
    <div className="space-y-2 border-t border-border-default px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t(`${I18N}.waiting`)}
        </span>
        <a
          href={handoff.url}
          {...(handoff.onPhone ? {} : { target: "_blank", rel: "noopener noreferrer" })}
          className="inline-flex items-center gap-1 text-xs text-brand-500 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          {t(`${I18N}.openAgain`)}
        </a>
      </div>

      {!handoff.onPhone && (
        <>
          <button
            type="button"
            onClick={handoff.toggleTrouble}
            aria-expanded={handoff.troubleOpen}
            className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary"
          >
            {handoff.troubleOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {t(`${I18N}.trouble`)}
          </button>
          {handoff.troubleOpen && (
            <TelegramManualFallback url={handoff.url} i18nPrefix={I18N} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * The profile-header nudge. Same one-tap link as the channels row — a chip
 * that opened a modal would advertise a shortcut and then not be one.
 *
 * It renders nothing until a link exists: the chip is a prompt, and a prompt
 * that cannot be acted on is worse than no prompt.
 */
function TelegramConnectChip({ handoff }: { handoff: TelegramHandoff }) {
  const { t } = useTranslation();
  if (!handoff.url) return null;

  const chip =
    "mt-2 inline-flex items-center gap-1.5 rounded-chip bg-[#26A5E4]/10 px-2 py-1 text-xs font-medium text-[#1B8FCA] transition-colors hover:bg-[#26A5E4]/20 dark:text-[#5CC1F0]";

  return (
    <a
      href={handoff.url}
      {...(handoff.onPhone ? {} : { target: "_blank", rel: "noopener noreferrer" })}
      onClick={handoff.markHandedOff}
      className={chip}
    >
      <TelegramIcon className="h-3.5 w-3.5 text-[#26A5E4]" />
      {handoff.handedOff
        ? t(`${I18N}.waiting`)
        : t("profile.channels.connectTelegram", "Connect Telegram")}
    </a>
  );
}

export { TelegramConnectButton, TelegramConnectChip, TelegramWaiting };
